import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { TemplateMetadata, listTemplates } from '../../../dms/templates/TemplateRegistry';

export type TemplateStatus = 'draft' | 'published';

export interface StyleTokens {
  fontFamily?: string;
  fontSize?: number;
  margins?: { top: number; right: number; bottom: number; left: number };
  pleadingPaper?: { enabled: boolean; lineNumberStart?: number; lineNumberEnd?: number; gutter?: number };
}

export interface TemplateRecord extends TemplateMetadata {
  status: TemplateStatus;
  usageCount?: number;
  styleTokens?: StyleTokens;
  captionStyle?: { titleBold?: boolean; titleItalic?: boolean };
}

interface TemplateStoreState {
  templates: TemplateRecord[];
  selectedId: string | null;
  defaults: Record<string, string>; // key: `${documentType}:${jurisdiction}:${format}` → templateId
  select: (id: string | null) => void;
  createFromBaseline: (baseline: TemplateMetadata) => string; // returns new id
  updateTemplate: (id: string, patch: Partial<TemplateRecord>) => void;
  publish: (id: string, published: boolean) => void;
  duplicate: (id: string) => string; // returns new id
  setAsDefault: (id: string) => void;
  loadInitial: () => void;
}

function storageKey() {
  return 'edgelex.templateBuilder.v1';
}

function persist(state: Pick<TemplateStoreState, 'templates' | 'defaults'>) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(state));
  } catch {}
}

function rehydrate(): Pick<TemplateStoreState, 'templates' | 'defaults'> | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const useTemplateStore = create<TemplateStoreState>()(
  devtools((set, get) => ({
    templates: [],
    selectedId: null,
    defaults: {},
    select: (id) => set({ selectedId: id }),
    createFromBaseline: (baseline) => {
      const id = `${baseline.id}__firm__${Date.now()}`;
      const record: TemplateRecord = {
        ...baseline,
        id,
        status: 'draft',
        usageCount: 0,
        styleTokens: {
          fontFamily: 'Times New Roman',
          fontSize: 12,
          margins: { top: 1, right: 1, bottom: 1, left: 1 },
          pleadingPaper: { enabled: baseline.format === 'pleading', lineNumberStart: 1, lineNumberEnd: 28, gutter: 0.5 },
        },
        captionStyle: { titleBold: true, titleItalic: false },
      };
      const templates: TemplateRecord[] = [...get().templates, record];
      set({ templates, selectedId: id });
      persist({ templates, defaults: get().defaults });
      return id;
    },
    updateTemplate: (id, patch) => {
      const templates: TemplateRecord[] = get().templates.map((t) => (t.id === id ? { ...(t as TemplateRecord), ...(patch as TemplateRecord) } : t));
      set({ templates });
      persist({ templates, defaults: get().defaults });
    },
    publish: (id, published) => {
      const templates: TemplateRecord[] = get().templates.map((t) => (t.id === id ? { ...(t as TemplateRecord), status: (published ? 'published' : 'draft') as TemplateStatus } : t));
      set({ templates });
      persist({ templates, defaults: get().defaults });
    },
    duplicate: (id) => {
      const src = get().templates.find((t) => t.id === id);
      if (!src) return '';
      const newId = `${src.id}__copy__${Date.now()}`;
      const copy: TemplateRecord = { ...(src as TemplateRecord), id: newId, status: 'draft', usageCount: 0 };
      const templates = [...get().templates, copy];
      set({ templates, selectedId: newId });
      persist({ templates, defaults: get().defaults });
      return newId;
    },
    setAsDefault: (id) => {
      const t = get().templates.find((x) => x.id === id);
      if (!t) return;
      const key = `${t.documentType}:${t.jurisdiction}:${t.format}`;
      const defaults = { ...get().defaults, [key]: id };
      set({ defaults });
      persist({ templates: get().templates, defaults });
    },
    loadInitial: () => {
      // Guard: if already loaded/non-empty, do nothing
      if ((get().templates || []).length > 0) return;

      const persisted = rehydrate();
      if (persisted) {
        const deduped = dedupeTemplates(persisted.templates as TemplateRecord[]);
        set({ templates: deduped, defaults: persisted.defaults || {} });
        persist({ templates: deduped, defaults: persisted.defaults || {} });
        return;
      }
      // Seed baselines only once per browser using a seed flag
      const seededFlagKey = 'edgelex.templateBuilder.seeded.v1';
      const alreadySeeded = safeGetLocalStorage(seededFlagKey) === 'true';
      if (alreadySeeded) return;

      const baselines = listTemplates();
      const seed: TemplateRecord[] = baselines.map((b) => ({ ...(b as any), status: 'draft' as TemplateStatus, usageCount: 0 }));
      const dedupedSeed = dedupeTemplates(seed);
      set({ templates: dedupedSeed });
      persist({ templates: dedupedSeed, defaults: {} });
      safeSetLocalStorage(seededFlagKey, 'true');
    },
  }))
);


// Helpers
function safeSetLocalStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function safeGetLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function dedupeTemplates(input: TemplateRecord[]): TemplateRecord[] {
  const seen: Record<string, TemplateRecord> = {};
  for (const t of input || []) {
    const key = `${t.name}__${t.jurisdiction}__${t.format}`;
    const existing = seen[key];
    if (!existing) {
      seen[key] = t;
      continue;
    }
    // Prefer published over draft, otherwise keep the first one
    if (existing.status !== 'published' && t.status === 'published') {
      seen[key] = t;
    }
  }
  return Object.values(seen);
}


